/**
 * 保存フォーマット（拠点ごとに名前を付けて保存したマッピング）のモデル（#4728）。
 *
 * 保存フォーマットは「マッピング画面の初期値の供給源」であって、マッピング画面を飛ばす
 * ショートカットではない。取り込み実行に使うのは常に画面の現在値で、取り込みジョブには
 * そのときのマッピング内容を丸ごと渡す（フォーマットを後から編集・削除しても、実行待ちの
 * 取り込みには影響しない）。
 *
 * サーバーには `setting`（SavedFormatSetting）を JSON のまま保存する。サーバーは中身を解釈しない。
 */

import { type ColumnMapping } from './column-mapping'
import { type CsvReadConfig, type EffectiveTable } from './csv-import-state'
import { LOGI_FIELDS, type LogiFieldValue } from './logi-fields'

/** 1 列分の紐づけ。header は保存時のヘッダー名（ヘッダー行が無い CSV では null）。 */
export type SavedFormatColumn = {
  header: string | null
  columnIndex: number
  field: LogiFieldValue
}

/** サーバーに保存する中身。形を変えるときは version を上げて読み込み側で旧形式も扱う。 */
export type SavedFormatSetting = {
  version: 1
  readConfig: CsvReadConfig
  columns: SavedFormatColumn[]
}

export type SavedFormat = {
  id: number
  name: string
  setting: SavedFormatSetting
}

const KNOWN_FIELDS = new Set<string>(LOGI_FIELDS.map((f) => f.value))

const isPositiveInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0

/**
 * 読み取り設定（行・列の番号）を確かめる。保存フォーマットは拠点の利用者で共有する任意の JSON なので、
 * 番号が負や小数の設定をそのまま使うと、実効テーブルを作るループが膨らんで画面が固まりうる。
 */
const parseReadConfig = (raw: unknown): CsvReadConfig | null => {
  if (typeof raw !== 'object' || raw === null) return null
  const value = raw as Partial<Record<keyof CsvReadConfig, unknown>>
  if (
    typeof value.hasHeader !== 'boolean' ||
    !isPositiveInteger(value.headerRowNo) ||
    !isPositiveInteger(value.startRowNo) ||
    !isPositiveInteger(value.startColNo)
  ) {
    return null
  }
  return {
    hasHeader: value.hasHeader,
    headerRowNo: value.headerRowNo,
    startRowNo: value.startRowNo,
    startColNo: value.startColNo
  }
}

/**
 * サーバーから受け取った setting（JSON）を型付きの値に直す。
 * 想定外の形（読み取り設定の行・列の番号が 1 以上の整数でない等）なら null を返し、
 * そのフォーマットは初期値として使わない。
 * 項目の定義から外れた紐づけ（取り込めなくなった項目）や、列の位置が 0 以上の整数でない紐づけは捨てる。
 */
export const parseSavedFormatSetting = (
  raw: unknown
): SavedFormatSetting | null => {
  if (typeof raw !== 'object' || raw === null) return null
  const value = raw as Partial<SavedFormatSetting>
  const readConfig = parseReadConfig(value.readConfig)
  if (value.version !== 1 || !readConfig || !Array.isArray(value.columns)) {
    return null
  }
  const columns = value.columns.filter(
    (c): c is SavedFormatColumn =>
      typeof c === 'object' &&
      c !== null &&
      isNonNegativeInteger(c.columnIndex) &&
      typeof c.field === 'string' &&
      KNOWN_FIELDS.has(c.field) &&
      (c.header === null || typeof c.header === 'string')
  )
  return { version: 1, readConfig, columns }
}

/**
 * 保存フォーマットを実効テーブルに適用してマッピングの初期値を作る。
 *
 * - 保存時にヘッダー名があり、今回の CSV にもヘッダー行があれば、ヘッダー名の一致で列を探す
 *   （列の並びが変わっても同じ項目に紐づく）。見つからない項目は紐づけない。
 * - ヘッダー名が無い（保存時か今回のどちらかにヘッダー行が無い）項目は、列の位置で紐づける。
 * - 1 項目 1 列の制約は保つ（同じ項目を 2 列に紐づけない）。
 */
export const applySavedFormat = (
  table: EffectiveTable,
  format: SavedFormat
): ColumnMapping => {
  const mapping: ColumnMapping = {}
  const usedFields = new Set<LogiFieldValue>()
  const usedColumns = new Set<number>()
  const hasHeaders = table.headers.length > 0

  for (const column of format.setting.columns) {
    if (usedFields.has(column.field)) continue
    let columnIndex: number | undefined
    if (column.header !== null && hasHeaders) {
      const found = table.headers.findIndex(
        (h, i) => h === column.header && !usedColumns.has(i)
      )
      columnIndex = found >= 0 ? found : undefined
    } else if (
      column.columnIndex < table.columnCount &&
      !usedColumns.has(column.columnIndex)
    ) {
      columnIndex = column.columnIndex
    }
    if (columnIndex === undefined) continue
    mapping[columnIndex] = column.field
    usedFields.add(column.field)
    usedColumns.add(columnIndex)
  }
  return mapping
}

/**
 * 保存フォーマットの列のうち、今回の CSV で見つからない列（applySavedFormat が紐づけられなかった列）。
 * 保存フォーマットは定義として扱うので、見つからない列があるときは取り込ませない（黙って外して取り込むと、
 * 外部キーのように列の有無で取込の動きが変わる項目で、なぜ取り込めたのか分からなくなる）。
 */
export const findMissingSavedColumns = (
  table: EffectiveTable,
  format: SavedFormat
): SavedFormatColumn[] => {
  const mapped = new Set(Object.values(applySavedFormat(table, format)))
  const seen = new Set<LogiFieldValue>()
  return format.setting.columns.filter((column) => {
    if (seen.has(column.field)) return false
    seen.add(column.field)
    return !mapped.has(column.field)
  })
}

/**
 * 今のマッピングが、保存フォーマットを当てたときから変わっているか。
 * 変わっていれば定義を直したことになるので、取り込む前にフォーマットの更新か別名での保存を求める。
 */
export const isMappingChangedFromSavedFormat = (
  table: EffectiveTable,
  format: SavedFormat,
  mapping: ColumnMapping
): boolean => {
  const applied = applySavedFormat(table, format)
  const keys = new Set([...Object.keys(applied), ...Object.keys(mapping)])
  for (const key of keys) {
    const index = Number(key)
    if ((applied[index] ?? null) !== (mapping[index] ?? null)) return true
  }
  return false
}

/** 現在の読み取り設定とマッピングから、保存フォーマットの中身を作る。 */
export const buildSavedFormatSetting = (
  table: EffectiveTable,
  readConfig: CsvReadConfig,
  mapping: ColumnMapping
): SavedFormatSetting => {
  const columns: SavedFormatColumn[] = []
  for (const [key, field] of Object.entries(mapping)) {
    if (field === null) continue
    const columnIndex = Number(key)
    const header = table.headers[columnIndex]
    columns.push({
      header: header !== undefined && header !== '' ? header : null,
      columnIndex,
      field
    })
  }
  columns.sort((a, b) => a.columnIndex - b.columnIndex)
  return { version: 1, readConfig, columns }
}

/** 保存フォーマットで取り込むときの、定義の確かめ */
export type SavedFormatCheck = {
  formatName: string
  /** CSV に見つからない保存列（見出し名。ヘッダーの無い列は「列N」） */
  missingColumns: string[]
  /** 保存フォーマットを当てたときから対応づけを変えたか */
  isEdited: boolean
  /** 取り込む前に保存し直す必要があるか（missingColumns があるか isEdited） */
  needsSave: boolean
  choice: SavedFormatSaveChoice | null
  newName: string
  /** 別名が拠点の既存フォーマットと重なっているか */
  isNewNameDuplicated: boolean
  onChangeChoice: (choice: SavedFormatSaveChoice) => void
  onChangeNewName: (name: string) => void
}

export type SavedFormatSaveChoice = 'update' | 'new'
